import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Mail, Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react'
import { Button } from '@components/ui/Button'
import { useAuthStore } from '@shared/stores/authStore'
import toast from 'react-hot-toast'

export const LoginPage = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [socialLoading, setSocialLoading] = useState(false)

  const navigate = useNavigate()
  const { login } = useAuthStore()

  const handleLogin = async () => {
    if (!email || !password) {
      toast.error('Please fill in all fields')
      return
    }

    setLoading(true)
    try {
      await login(email, password)
      toast.success('Login successful! Welcome back!')
      navigate('/')
    } catch (error: any) {
      toast.error('Login failed: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleFacebookLogin = async () => {
    setSocialLoading(true)
    try {
      await new Promise(resolve => setTimeout(resolve, 1500))
      toast.success('Facebook login successful!')
      navigate('/')
    } catch (error: any) {
      toast.error('Facebook login failed: ' + error.message)
    } finally {
      setSocialLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    setSocialLoading(true)
    try {
      await new Promise(resolve => setTimeout(resolve, 1500))
      toast.success('Google login successful!')
      navigate('/')
    } catch (error: any) {
      toast.error('Google login failed: ' + error.message)
    } finally {
      setSocialLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <Link to="/onboarding" className="inline-flex items-center text-white mb-4">
            <ArrowLeft size={18} className="sm:w-5 sm:h-5" />
            <span className="ml-2 text-sm sm:text-base">Back</span>
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Welcome Back!</h1>
          <p className="text-primary-100 text-sm sm:text-base">Sign in to continue to Media X</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
          <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }} className="space-y-3 sm:space-y-4">
            {/* Email Input */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-light" size={18} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full pl-9 sm:pl-10 pr-4 py-2.5 sm:py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base"
                  required
                />
              </div>
            </div>

            {/* Password Input */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-light" size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full pl-9 sm:pl-10 pr-10 sm:pr-12 py-2.5 sm:py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-light hover:text-text-primary"
                >
                  {showPassword ? <EyeOff size={18} className="sm:w-5 sm:h-5" /> : <Eye size={18} className="sm:w-5 sm:h-5" />}
                </button>
              </div>
            </div>

            {/* Forgot Password */}
            <div className="text-right">
              <Link to="/forgot-password" className="text-xs sm:text-sm text-primary-500 hover:text-primary-600">
                Forgot Password?
              </Link>
            </div>

            {/* Login Button */}
            <Button
              title={loading ? 'Signing in...' : 'Sign In'}
              onPress={handleLogin}
              variant="primary"
              size="large"
              loading={loading}
              className="w-full"
            />
          </form>

          {/* Divider */}
          <div className="my-4 sm:my-6 flex items-center">
            <div className="flex-1 border-t border-border"></div>
            <span className="px-3 sm:px-4 text-xs sm:text-sm text-text-secondary">or</span>
            <div className="flex-1 border-t border-border"></div>
          </div>

          {/* Social Login Buttons */}
          <div className="space-y-2 sm:space-y-3">
            <button
              onClick={handleFacebookLogin}
              disabled={socialLoading}
              className="w-full flex items-center justify-center space-x-2 sm:space-x-3 bg-blue-600 text-white py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <div className="w-4 h-4 sm:w-5 sm:h-5 bg-white rounded flex items-center justify-center">
                <span className="text-blue-600 text-xs font-bold">f</span>
              </div>
              <span className="font-medium text-sm sm:text-base">
                {socialLoading ? 'Connecting...' : 'Continue with Facebook'}
              </span>
            </button>

            <button
              onClick={handleGoogleLogin}
              disabled={socialLoading}
              className="w-full flex items-center justify-center space-x-2 sm:space-x-3 bg-white text-text-primary border border-border py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg hover:bg-border-light transition-colors disabled:opacity-50"
            >
              <div className="w-4 h-4 sm:w-5 sm:h-5">
                <svg viewBox="0 0 24 24" className="w-4 h-4 sm:w-5 sm:h-5">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <span className="font-medium text-sm sm:text-base">
                {socialLoading ? 'Connecting...' : 'Continue with Google'}
              </span>
            </button>
          </div>

          {/* Sign Up Link */}
          <div className="mt-4 sm:mt-6 text-center">
            <p className="text-xs sm:text-sm text-text-secondary">
              Don't have an account?{' '}
              <Link to="/signup" className="text-primary-500 hover:text-primary-600 font-medium">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
} 